import type { Locale } from "@commerce-frontend/i18n/types";
import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import { type ShopSectionFragmentFragment, graphql } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { typography } from "@commerce-frontend/ui/base/Typography/typography";
import {
	Carousel,
	CarouselContent,
	CarouselNext,
	CarouselPrevious,
} from "@commerce-frontend/ui/components/Carousel/Carousel";
import { ProductGrid } from "@commerce-frontend/ui/components/ProductGrid/ProductGrid";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import { useTranslations } from "next-intl";
import type { HTMLAttributes } from "react";
import { ProductCard } from "~/components/ProductCard/ProductCard";
import { productDetailHref } from "~/lib/link-builder";

graphql(/* GraphQL */ `
	fragment ShopSectionFragment on ShopSection {
		__typename
		id
		title
		subtitle
		category
		viewAllLink {
			url
			newTab
		}
		viewAllLabel
		variant
		products {
			...ProductCard
		}
	}
`);

type Props = {
	data: ShopSectionFragmentFragment;
	locale: Locale;
} & HTMLAttributes<HTMLDivElement>;

export const ShopSection = ({ data, locale }: Props) => {
	const t = useTranslations("ShopSection");
	const { category, title, subtitle, products, viewAllLabel, viewAllLink, variant } = data;

	return (
		<div className="flex flex-col gap-4xl lg:gap-6xl" {...storyblokDataAttributes(data)}>
			<header className="flex justify-between">
				<div className="flex flex-col gap-md">
					<div className="flex flex-col gap-lg">
						{category && <div className="font-semibold text-brand-700">{category}</div>}
						<h2 className={typography("h2")}>{title}</h2>
					</div>
					{subtitle && <p className={typography("lead")}>{subtitle}</p>}
				</div>
				{viewAllLabel && viewAllLink?.url ? (
					<Button
						href={viewAllLink.url}
						target={viewAllLink.newTab ? "_blank" : undefined}
						className="self-end"
						variant="tertiaryColor"
					>
						{viewAllLabel}
					</Button>
				) : null}
			</header>
			{variant === "grid" ? (
				<ProductGrid>
					{products?.map((product) => (
						<Link key={product.id} href={productDetailHref(product.slug)}>
							<ProductCard product={product} locale={locale} />
						</Link>
					))}
				</ProductGrid>
			) : (
				<Carousel
					className="grow overflow-hidden max-lg:-ml-[calc(1rem+2px)] max-lg:-mr-[calc(1rem)]"
					opts={{ slidesToScroll: "auto" }}
				>
					<div className="relative">
						<CarouselContent className="max-lg:px-container-sm">
							{products?.map((product) => (
								<Link
									className="flex w-40 h-full shrink-0 flex-col lg:w-64"
									key={product.id}
									href={productDetailHref(product.slug)}
								>
									<ProductCard product={product} locale={locale} />
								</Link>
							))}
						</CarouselContent>
						<CarouselNext />
						<CarouselPrevious />
					</div>
				</Carousel>
			)}
		</div>
	);
};
